/**
 * VoicePlayerBar — sticky плашка сверху чата с активным голосовым.
 * play/pause + prev/next + mute + speed + close.
 */
import { useVoicePlayer } from "@/contexts/VoicePlayerContext";
import { parseBackendIso } from "@/lib/presence";

function fmtTime(s: number): string {
  if (!isFinite(s) || s < 0) return "00:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function fmtBarDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = parseBackendIso(iso);
    if (!d || Number.isNaN(d.getTime())) return "";
    const today = new Date();
    const isSameDay = d.toDateString() === today.toDateString();
    const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    if (isSameDay) return `сегодня ${time}`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm} ${time}`;
  } catch { return ""; }
}

export function VoicePlayerBar() {
  const {
    meta, playing, currentTime, duration, playbackRate, muted,
    togglePlayPause, cyclePlaybackRate, toggleMute, playPrev, playNext, close,
  } = useVoicePlayer();
  if (!meta) return null;

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const remaining = Math.max(0, duration - currentTime);

  return (
    <div className="voice-player-bar">
      <button
        type="button"
        className="voice-player-bar-nav"
        onClick={playPrev}
        aria-label="Предыдущее"
        title="Предыдущее голосовое"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
          <path d="M14 4v12L8 10l6-6z"/>
          <rect x="5" y="4" width="2" height="12" rx="1"/>
        </svg>
      </button>
      <button
        type="button"
        className="voice-player-bar-playbtn"
        onClick={togglePlayPause}
        aria-label={playing ? "Пауза" : "Воспроизвести"}
      >
        {playing ? (
          <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
            <rect x="5" y="3" width="4" height="14" rx="1"/>
            <rect x="11" y="3" width="4" height="14" rx="1"/>
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
            <path d="M5 3l12 7-12 7V3z"/>
          </svg>
        )}
      </button>
      <button
        type="button"
        className="voice-player-bar-nav"
        onClick={playNext}
        aria-label="Следующее"
        title="Следующее голосовое"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
          <path d="M6 4l6 6-6 6V4z"/>
          <rect x="13" y="4" width="2" height="12" rx="1"/>
        </svg>
      </button>
      <div className="voice-player-bar-body">
        <div className="voice-player-bar-title">
          <span className="voice-player-bar-author">{meta.authorName}</span>
          <span className="voice-player-bar-date">{fmtBarDate(meta.ts)}</span>
        </div>
        <div className="voice-player-bar-progress">
          <div className="voice-player-bar-progress-fill" style={{ width: `${progress}%` }}/>
        </div>
      </div>
      <span className="voice-player-bar-time">{fmtTime(remaining)}</span>
      <button
        type="button"
        className="voice-player-bar-mute"
        onClick={toggleMute}
        aria-label={muted ? "Включить звук" : "Выключить звук"}
        title={muted ? "Включить звук" : "Выключить звук"}
      >
        {muted ? (
          <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8v4h3l4 3V5L6 8H3z" fill="currentColor"/>
            <path d="M15 7l4 4M19 7l-4 4"/>
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8v4h3l4 3V5L6 8H3z" fill="currentColor"/>
            <path d="M14 7c1.2 1 1.8 2 1.8 3s-.6 2-1.8 3"/>
            <path d="M16.5 5c2 1.5 3 3 3 5s-1 3.5-3 5"/>
          </svg>
        )}
      </button>
      <button
        type="button"
        className="voice-player-bar-rate"
        onClick={cyclePlaybackRate}
        title="Скорость воспроизведения"
      >
        {playbackRate === 1 ? "1x" : playbackRate === 1.5 ? "1.5x" : "2x"}
      </button>
      <button
        type="button"
        className="voice-player-bar-close"
        onClick={close}
        aria-label="Закрыть плеер"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M5 5l10 10M15 5L5 15"/>
        </svg>
      </button>
    </div>
  );
}
